// src/AssetDashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";

import CandlestickChart from "../screens/CandlestickChart";
import DynamicDmmChart from "../screens/DynamicDmmChart";

import { apiFetch } from "../api";
import { useSettings } from "../settings";

const PERIOD_OPTIONS = [
  { label: "1 mês", days: 30 },
  { label: "3 meses", days: 90 },
  { label: "6 meses", days: 180 },
  { label: "1 ano", days: 365 },
  { label: "2 anos", days: 365 * 2 },
  { label: "Todo", days: null },
];

function normalizeDate(d) {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toISOString().slice(0, 10);
}

function findPeriodByLabel(label) {
  return PERIOD_OPTIONS.find((p) => p.label === label) || PERIOD_OPTIONS[2];
}

function findPeriodByDays(days) {
  if (days == null) return PERIOD_OPTIONS.find((p) => p.days == null) || PERIOD_OPTIONS[2];
  return PERIOD_OPTIONS.find((p) => p.days === Number(days)) || PERIOD_OPTIONS[2];
}

function fmtMoney(x, decimals = 2) {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `R$ ${n.toFixed(decimals).replace(".", ",")}`;
}

function fmtPct(x01, decimals = 2) {
  if (x01 == null) return "—";
  const n = Number(x01);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(decimals).replace(".", ",")}%`;
}

function fmtPctSigned01(x01, { digits = 1 } = {}) {
  if (x01 == null) return "—";
  const n = Number(x01);
  if (!Number.isFinite(n)) return "—";
  const p = n * 100;
  const sign = p > 0 ? "+" : p < 0 ? "−" : "";
  const abs = Math.abs(p);
  const val = abs < 0.05 ? 0 : abs;
  const s = val.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${sign}${s}%`;
}

function normSymbol(sym) {
  if (!sym) return "";
  return String(sym).trim().toUpperCase().replace(/^\$/, "").replace(/\.SA$/, "");
}

/**
 * ✅ IMPORTANT:
 * - BottomTabsLayout é o ÚNICO dono do scroll (overflowY:auto) nas telas com tabs.
 * - Charts costumam "roubar" wheel/touch e travar scroll.
 * - Este guard corta wheel/touch ANTES de chegar no chart (capture phase),
 *   permitindo que o container scrollável role normalmente.
 */
function ChartGuard({ children, style }) {
  const stop = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      style={{ ...styles.chartBox, ...style }}
      onWheelCapture={stop}
      onTouchMoveCapture={stop}
      onPointerMoveCapture={stop}
    >
      {children}
    </div>
  );
}

export default function AssetDashboard() {
  // ✅ AssetDashboard é full-screen (sem BottomTabsLayout)
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();

  const rfAnnualSafe = Number.isFinite(Number(settings?.rfAnnual)) ? Number(settings.rfAnnual) : 0;

  // ✅ símbolo SEMPRE vem na query
  const selectedSymbol = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normSymbol(params.get("symbol"));
  }, [location.search]);

  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const params = new URLSearchParams(location.search);
    const period = params.get("period");
    const days = params.get("days");
    if (period) return findPeriodByLabel(period);
    if (days != null) return findPeriodByDays(days);

    const saved = localStorage.getItem("finbot_period_label");
    return saved ? findPeriodByLabel(saved) : PERIOD_OPTIONS[2];
  });

  const [priceData, setPriceData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dynamics, setDynamics] = useState(null);
  const [simulation, setSimulation] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeRange, setTimeRange] = useState(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 720
  );

  useEffect(() => {
    function recompute() {
      setIsMobile(window.innerWidth <= 720);
    }
    window.addEventListener("resize", recompute);
    window.addEventListener("orientationchange", recompute);
    recompute();
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("orientationchange", recompute);
    };
  }, []);

  // ✅ URL manda no período
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const period = params.get("period");
    const days = params.get("days");

    if (period) {
      const p = findPeriodByLabel(period);
      if (p.label !== selectedPeriod.label) setSelectedPeriod(p);
      return;
    }

    if (days != null) {
      const p = findPeriodByDays(days);
      if (p.label !== selectedPeriod.label) setSelectedPeriod(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  function writePeriodToUrl(nextPeriod) {
    const params = new URLSearchParams(location.search);
    params.set("symbol", selectedSymbol);
    params.set("period", nextPeriod.label);
    params.delete("days");
    navigate(`/assets?${params.toString()}`, { replace: true, state: location.state });
  }

  // ----------------- "Voltar" inteligente -----------------
  const from = location.state?.from || null;

  function goBackSmart() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (from) {
      navigate(from, { replace: true });
      return;
    }
    navigate("/monitoring", { replace: true });
  }

  // ✅ memoiza dynQuery (evita depender de `settings` inteiro no efeito de fetch)
  const dynQuery = useMemo(() => {
    const q = new URLSearchParams();

    if (selectedPeriod.days != null) q.set("days", String(selectedPeriod.days));
    q.set("rfAnnual", String(rfAnnualSafe || 0));

    // params de dynamics (se existirem)
    const entries = {
      vEmaWindow: settings?.vEmaWindow,
      aEmaWindow: settings?.aEmaWindow,
      topNDays: settings?.topNDays,
      requireMinBullDays: settings?.requireMinBullDays,
      minDmmRelForTop: settings?.minDmmRelForTop,
      vRelMax: settings?.vRelMax,
      aRelMax: settings?.aRelMax,
      vRelDeadband: settings?.vRelDeadband,
      usePeakDropRule: settings?.usePeakDropRule,
      peakDropPct: settings?.peakDropPct,
    };

    for (const [k, v] of Object.entries(entries)) {
      if (v == null) continue;
      if (typeof v === "boolean") q.set(k, v ? "true" : "false");
      else q.set(k, String(v));
    }

    const qs = q.toString();
    return qs ? `?${qs}` : "";
  }, [
    selectedPeriod.days,
    rfAnnualSafe,
    settings?.vEmaWindow,
    settings?.aEmaWindow,
    settings?.topNDays,
    settings?.requireMinBullDays,
    settings?.minDmmRelForTop,
    settings?.vRelMax,
    settings?.aRelMax,
    settings?.vRelDeadband,
    settings?.usePeakDropRule,
    settings?.peakDropPct,
  ]);

  // ----------------- Load prices + summary + dynamics + simulation -----------------
  useEffect(() => {
    if (!selectedSymbol) {
      setError("Símbolo ausente na URL. Abra o ativo por um link.");
      setPriceData([]);
      setSummary(null);
      setDynamics(null);
      setSimulation(null);
      return;
    }

    let alive = true;

    async function fetchAll() {
      setLoading(true);
      setError("");

      try {
        const days = selectedPeriod.days;

        const qBase = new URLSearchParams();
        if (days != null) qBase.set("days", String(days));
        qBase.set("rfAnnual", String(rfAnnualSafe));
        const baseQuery = qBase.toString() ? `?${qBase.toString()}` : "";

        const [pricesJson, summaryJson, dynamicsJson, simJson] = await Promise.all([
          apiFetch(`/api/assets/${encodeURIComponent(selectedSymbol)}/prices${baseQuery}`),
          apiFetch(`/api/assets/${encodeURIComponent(selectedSymbol)}/summary${baseQuery}`),
          apiFetch(`/api/assets/${encodeURIComponent(selectedSymbol)}/dynamics${dynQuery}`),
          apiFetch(`/api/assets/${encodeURIComponent(selectedSymbol)}/simulation${baseQuery}`),
        ]);

        if (!alive) return;

        const rawPrices = Array.isArray(pricesJson) ? pricesJson : [];
        const priceNorm = rawPrices.map((p, idx, arr) => {
          const prev = idx > 0 ? arr[idx - 1] : p;
          const close = Number(p.close);
          const prevClose = Number(prev.close);
          const change = close - prevClose;

          return {
            ...p,
            date: normalizeDate(p.date),
            open: p.open != null ? Number(p.open) : null,
            high: p.high != null ? Number(p.high) : null,
            low: p.low != null ? Number(p.low) : null,
            close,
            volume: p.volume != null ? Number(p.volume) : null,
            mm10: p.mm10 != null ? Number(p.mm10) : null,
            mm40: p.mm40 != null ? Number(p.mm40) : null,
            signal: p.signal ? String(p.signal).toUpperCase() : "HOLD",
            priceChange: change,
            volumeColor: change >= 0 ? "#26a69a" : "#ef5350",
          };
        });

        setPriceData(priceNorm);
        setSummary(summaryJson ?? null);
        setDynamics(dynamicsJson ?? null);
        setSimulation(simJson ?? null);

        localStorage.setItem("finbot_period_label", selectedPeriod.label);
      } catch (err) {
        console.error(err);
        if (!alive) return;
        setError("Falha ao carregar dados do ativo.");
        setSummary(null);
        setDynamics(null);
        setSimulation(null);
        setPriceData([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    fetchAll();
    return () => {
      alive = false;
    };
  }, [selectedSymbol, selectedPeriod, rfAnnualSafe, dynQuery]);

  // ----------------- derived UI data -----------------
  const lastPrice = useMemo(() => {
    if (!priceData.length) return null;
    return priceData[priceData.length - 1];
  }, [priceData]);

  const asof = summary?.period?.endDate ?? lastPrice?.date ?? null;
  const close = summary?.price?.endClose ?? lastPrice?.close ?? null;

  // ✅ fonte única: vem do summary.regime (evita divergência com Monitoring)
  const distToMM10 = summary?.regime?.distToMM10 ?? null;
  const lastDailyChangePct = summary?.price?.lastDailyChangePct ?? null;

  const head = useMemo(() => {
    let text = "—";
    let kind = "none";

    if (distToMM10 != null && Number.isFinite(Number(distToMM10))) {
      const d = Number(distToMM10);
      if (Math.abs(d) < 0.0005) {
        text = "Na média recente";
        kind = "none";
      } else {
        text = `${fmtPctSigned01(d, { digits: 1 })} da média recente`;
        kind = d > 0 ? "buy" : "sell";
      }
    }

    return { text, kind };
  }, [distToMM10]);

  function resetZoom() {
    setTimeRange(null);
  }

  // simulation derived (mantém compat com o que você já tinha)
  const simA = simulation?.metrics?.a ?? null;
  const simB = simulation?.metrics?.b ?? null;
  const simC = simulation?.metrics?.c ?? null;

  const dynSeries = Array.isArray(dynamics?.series) ? dynamics.series : [];

  const cardsTopStyle = useMemo(
    () => ({ ...styles.cardsTop, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(280px, 1fr))" }),
    [isMobile]
  );

  const cardsBottomStyle = useMemo(
    () => ({ ...styles.cardsBottom, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(280px, 1fr))" }),
    [isMobile]
  );

  // ----------------- Render -----------------
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={goBackSmart} style={styles.backBtn} title="Voltar" type="button">
            <FiChevronLeft size={18} /> Voltar
          </button>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, letterSpacing: 0.2 }}>{selectedSymbol || "Ativo"}</h2>
            {asof ? <Pill>as-of: {asof}</Pill> : null}
            {loading ? <Pill>carregando…</Pill> : null}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={resetZoom}
            style={{ ...styles.btn, ...styles.btnGhost }}
            title="Limpar zoom / seleção de tempo"
            type="button"
          >
            Reset zoom
          </button>
        </div>
      </header>

      {/* PERÍODO */}
      <section style={{ marginTop: 10 }}>
        <div style={styles.periodRow}>
          {PERIOD_OPTIONS.map((p) => {
            const active = p.label === selectedPeriod.label;
            return (
              <button
                key={p.label}
                onClick={() => {
                  setSelectedPeriod(p);
                  writePeriodToUrl(p);
                }}
                style={{ ...styles.btn, ...(active ? styles.btnPrimary : styles.btnGhost) }}
                type="button"
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </section>

      {error ? <div style={styles.error}>⚠ {error}</div> : null}

      {/* CARDS (Top) */}
      <section style={{ marginTop: 12 }}>
        <div style={cardsTopStyle}>
          <InfoCard title="Último fechamento">
            <div
              style={{
                ...styles.cardHeadline,
                ...(head.kind === "buy" ? styles.headBuy : null),
                ...(head.kind === "sell" ? styles.headSell : null),
              }}
            >
              {head.text}
            </div>

            <div style={styles.cardMainValue}>{close != null ? fmtMoney(close) : "—"}</div>

            <div style={styles.cardMetaLine}>
              <span style={styles.metaValue}>
                {/* {close != null ? `${fmtMoney(close)} · último fechamento` : "—"} */}
                {lastDailyChangePct != null ? ` (${fmtPctSigned01(lastDailyChangePct, { digits: 1 })})` : ""}
              </span>
            </div>

            <div style={styles.cardMetaLine}>
              <span style={styles.metaLabel}>Data:</span>{" "}
              <span style={styles.metaValue}>{asof ?? "—"}</span>
            </div>
          </InfoCard>

          <InfoCard title="Volatilidade">
            <div style={styles.cardMainValue}>
              {summary?.price?.volAnnual != null ? fmtPct(summary.price.volAnnual, 2) : "—"}
            </div>

            <div style={styles.cardMetaLine}>
              <span style={styles.metaLabel}>Estimativa:</span>{" "}
              <span style={styles.metaValue}>anual (base 252d)</span>
            </div>

            <div style={styles.cardMetaLine}>
              <span style={styles.metaLabel}>Risco diário (ATR):</span>{" "}
              <span style={styles.metaValue}>
                {summary?.price?.atrPct != null ? fmtPct(summary.price.atrPct, 2) : "—"}
              </span>
            </div>
          </InfoCard>
        </div>

        {/* CARDS (Bottom) — mantém por enquanto, mas já está “enxuto” */}
        <div style={{ marginTop: 12 }}>
          <div style={cardsBottomStyle}>
            <InfoCard title="Retornos no período">
              <div style={{ display: "grid", gap: 10 }}>
                <div style={styles.kpiChip}>
                  <span style={{ opacity: 0.75 }}>Pré-Vale/Pré-Topo:</span>{" "}
                  <b>{fmtPct(simB?.totalReturn, 2)}</b>
                </div>

                <div style={styles.kpiChip}>
                  <span style={{ opacity: 0.75 }}>Vale/Topo:</span>{" "}
                  <b>{fmtPct(simC?.totalReturn, 2)}</b>
                </div>

                <div style={styles.kpiChip}>
                  <span style={{ opacity: 0.75 }}>Buy&Hold:</span>{" "}
                  <b>{fmtPct(summary?.price?.buyHoldReturn, 2)}</b>
                </div>
              </div>

              <div style={{ ...styles.cardMetaLine, marginTop: 10 }}>
                <span style={styles.metaValue}>
                  {summary?.period?.startDate && summary?.period?.endDate
                    ? `${summary.period.startDate} → ${summary.period.endDate}`
                    : "—"}
                </span>
              </div>
            </InfoCard>

            <InfoCard title="Risco e atividade (estratégia)">
              <div style={{ display: "grid", gap: 10 }}>
                <div style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>Max Drawdown</span>
                  <span style={styles.kpiValue}>{simA?.maxDrawdown != null ? fmtPct(simA.maxDrawdown, 1) : "—"}</span>
                </div>

                <div style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>Win-rate (dias +)</span>
                  <span style={styles.kpiValue}>{simA?.winRateDays != null ? fmtPct(simA.winRateDays, 1) : "—"}</span>
                </div>

                <div style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>Trades / Round-trips</span>
                  <span style={styles.kpiValue}>
                    {simA?.nTrades != null ? simA.nTrades : "—"} / {simA?.nRoundTrips != null ? simA.nRoundTrips : "—"}
                  </span>
                </div>

                <div style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>Win-rate (trades)</span>
                  <span style={styles.kpiValue}>
                    {simA?.winRateTrades != null ? fmtPct(simA.winRateTrades, 1) : "—"}
                  </span>
                </div>
              </div>

              <div style={{ ...styles.cardMetaLine, marginTop: 10 }}>
                <span style={styles.metaLabel}>Obs.:</span>{" "}
                <span style={styles.metaValue}>métricas calculadas a partir do snapshot persistido.</span>
              </div>
            </InfoCard>
          </div>
        </div>
      </section>

      {/* GRÁFICOS */}
      <section style={{ marginTop: 14 }}>
        <h3 style={styles.sectionTitle}>Preço e volume</h3>
        <div style={styles.chartCard}>
          <ChartGuard>
            <CandlestickChart
              data={priceData}
              symbol={selectedSymbol}
              syncedTimeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </ChartGuard>
        </div>
      </section>

      <section style={{ marginTop: 14 }}>
        <h3 style={styles.sectionTitle}>Tendência e dinâmica</h3>
        <div style={styles.chartCard}>
          <ChartGuard>
            <DynamicDmmChart data={dynSeries} syncedTimeRange={timeRange} onTimeRangeChange={setTimeRange} />
          </ChartGuard>
        </div>
      </section>
    </div>
  );
}

/* ---------- UI bits ---------- */
function InfoCard({ title, children }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.cardValue}>{children}</div>
    </div>
  );
}

function Pill({ children }) {
  return <span style={styles.pill}>{children}</span>;
}

/* ---------- styles ---------- */
const styles = {
  page: { padding: 16, maxWidth: 1200, margin: "0 auto" },

  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },

  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    padding: "8px 10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: 1,
  },

  periodRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },

  btn: {
    borderRadius: 999,
    padding: "8px 12px",
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: 1,
    transition: "transform 120ms ease, background 120ms ease",
  },
  btnGhost: { background: "rgba(255,255,255,0.06)" },
  btnPrimary: { background: "rgba(80,200,120,0.12)", border: "1px solid rgba(80,200,120,0.35)" },

  pill: {
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
    opacity: 0.92,
  },

  cardsTop: { display: "grid", gridTemplateColumns: "repeat(2, minmax(280px, 1fr))", gap: 12 },
  cardsBottom: { display: "grid", gridTemplateColumns: "repeat(2, minmax(280px, 1fr))", gap: 12 },

  card: {
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
  },
  cardTitle: { opacity: 0.75, fontSize: 12, marginBottom: 10 },
  cardValue: { fontSize: 14, fontWeight: 600, lineHeight: 1.25 },

  cardHeadline: { fontSize: 12, fontWeight: 800, opacity: 0.9, marginBottom: 6 },
  cardMainValue: { fontSize: 26, fontWeight: 900, letterSpacing: 0.2, marginTop: 2 },

  cardMetaLine: { marginTop: 10, fontSize: 12, opacity: 0.9 },
  metaLabel: { opacity: 0.7 },
  metaValue: { opacity: 0.92, fontWeight: 700 },

  headBuy: { color: "rgba(150,255,190,0.95)" },
  headSell: { color: "rgba(255,150,150,0.95)" },

  kpiChip: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    fontSize: 13,
  },

  kpiRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 },
  kpiLabel: { fontSize: 12, opacity: 0.72, fontWeight: 700 },
  kpiValue: { fontSize: 16, fontWeight: 900, letterSpacing: 0.15 },

  error: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,100,100,0.25)",
    background: "rgba(255,100,100,0.06)",
  },

  sectionTitle: { margin: "10px 0" },

  chartCard: {
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
  },

  chartBox: {
    width: "100%",
    height: 360,
    position: "relative",
    contain: "layout paint",
    touchAction: "pan-y",
  },
};
